import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useWebRTC } from '../../context/WebRTCContext';
import { calcRoundWinners } from '../../utils/scoring';
import { startRound, endGame } from '../../services/firebase/games';
import { logError } from '../../utils/logger';
import { CHATTERS_ID } from '../../constants/game';
import { getColCount } from '../../utils/videoGrid';
import PlayerVideoCard, { KickDialog } from '../../components/PlayerVideoCard';

function calcPlayerStats(uid, scores) {
  const s = scores[uid] || {};
  const actualScores = {};
  for (const [id, data] of Object.entries(scores)) {
    if (data.actualScore !== undefined) actualScores[id] = data.actualScore;
  }
  let totalErr = 0, errCount = 0;
  for (const [id, predicted] of Object.entries(s.predictions || {})) {
    if (actualScores[id] !== undefined) {
      totalErr += Math.abs(predicted - actualScores[id]);
      errCount++;
    }
  }
  const avgErrNum = errCount > 0 ? totalErr / errCount : null;
  const avgErr = avgErrNum !== null ? `${Math.round((1 - avgErrNum / 10) * 100)}%` : '-';
  const actualScore = s.actualScore ?? '-';
  return { avgErr, avgErrNum, actualScore };
}

function ordinal(n) {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

export default function RoundResults({ game, round, scores, isHost, playerAvatars, isChatter = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const webrtc = useWebRTC();
  const hasWebRTC = !!webrtc && !game.inPersonMode;

  const {
    cameraEnabled, micEnabled, toggleCamera, toggleMic,
    localCameraStream, remoteStreamsByUid, playerMediaStates,
    localMutes, localVolumes, localHidden,
    muteRemote, unmuteRemote, setRemoteVolume,
    hideRemoteCamera, showRemoteCamera,
  } = webrtc || {};

  function webrtcCardProps(uid) {
    if (!hasWebRTC) return {};
    const isMe = uid === user?.uid;
    return {
      mediaState: playerMediaStates?.[uid],
      localCameraStream: isMe ? localCameraStream : null,
      remoteStreamsByUid: !isMe ? remoteStreamsByUid?.get(uid) : null,
      cameraStreamId: playerMediaStates?.[uid]?.cameraStreamId,
      myCameraEnabled: isMe ? cameraEnabled : undefined,
      myMicEnabled: isMe ? micEnabled : undefined,
      toggleCamera: isMe ? toggleCamera : undefined,
      toggleMic: isMe ? toggleMic : undefined,
      isMuted: !isMe ? (localMutes?.[uid] ?? false) : false,
      volume: !isMe ? (localVolumes?.[uid] ?? 1) : 1,
      isHidden: !isMe ? (localHidden?.[uid] ?? false) : false,
      onMuteToggle: !isMe ? () => (localMutes?.[uid] ? unmuteRemote?.(uid) : muteRemote?.(uid)) : null,
      onVolumeChange: !isMe ? (v) => setRemoteVolume?.(uid, v) : null,
      onHideToggle: !isMe ? () => (localHidden?.[uid] ? showRemoteCamera?.(uid) : hideRemoteCamera?.(uid)) : null,
      onKick: isHost && !isMe ? () => setKickConfirmUid(uid) : null,
    };
  }

  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endGameConfirm, setEndGameConfirm] = useState(false);
  const [kickConfirmUid, setKickConfirmUid] = useState(null);
  const [kicking, setKicking] = useState(false);

  const [isSm, setIsSm] = useState(() => window.innerWidth >= 640);
  useEffect(() => {
    const handler = () => setIsSm(window.innerWidth >= 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [showStats, setShowStats] = useState(() => {
    try {
      const saved = localStorage.getItem('tmg_show_round_stats');
      return saved === null ? false : saved === 'true';
    } catch { return false; }
  });

  const cardsAreaRef = useRef(null);
  const [cardsAreaH, setCardsAreaH] = useState(0);

  useEffect(() => {
    if (showStats) return;
    const el = cardsAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setCardsAreaH(entries[0].contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [showStats]);

  function toggleStats() {
    setShowStats(prev => {
      const next = !prev;
      try { localStorage.setItem('tmg_show_round_stats', String(next)); } catch { /* storage unavailable */ }
      return next;
    });
  }

  const winners = calcRoundWinners(game.playerIds, scores);

  function getWinnerText() {
    if (winners.length >= 3) return t('gameOver.winnerTextCheating');
    if (winners.length === 2) return t('gameOver.winnerTextTwo');
    const texts = t('gameOver.winnerTexts', { returnObjects: true });
    if (!Array.isArray(texts) || texts.length === 0) return '';
    let hash = 0;
    for (let i = 0; i < round.id.length; i++) {
      hash = ((hash << 5) - hash) + round.id.charCodeAt(i);
      hash |= 0;
    }
    return texts[Math.abs(hash) % texts.length];
  }
  const winnerText = getWinnerText();

  const regularPlayers = game.playerIds.filter(id => id !== CHATTERS_ID);
  const nonWinners = useMemo(() => {
    const filtered = regularPlayers.filter(id => !winners.includes(id));
    return [...filtered].sort((a, b) => {
      const errA = calcPlayerStats(a, scores).avgErrNum ?? Infinity;
      const errB = calcPlayerStats(b, scores).avgErrNum ?? Infinity;
      if (errA !== errB) return errA - errB;
      return filtered.indexOf(a) - filtered.indexOf(b);
    });
  }, [regularPlayers, winners, scores]);
  const gridCols = nonWinners.length === 2 ? 2 : getColCount(nonWinners.length, isSm);

  const nonWinnerPositions = useMemo(() => {
    const withErr = nonWinners.map(uid => ({ uid, err: calcPlayerStats(uid, scores).avgErrNum ?? Infinity }));
    withErr.sort((a, b) => a.err - b.err);
    const map = {};
    let pos = winners.length + 1;
    for (let i = 0; i < withErr.length; i++) {
      if (i > 0 && withErr[i].err !== withErr[i - 1].err) pos = winners.length + 1 + i;
      map[withErr[i].uid] = pos;
    }
    return map;
  }, [nonWinners, scores, winners.length]);

  const tableRows = [
    ...game.playerIds.filter(id => winners.includes(id)),
    ...game.playerIds.filter(id => !winners.includes(id)),
  ];

  // Compute a unified card width so winner and non-winner cards are the same size.
  // Measures the full cards area height and solves for a cardH that fills it exactly.
  const unifiedCardW = useMemo(() => {
    if (showStats || cardsAreaH <= 0) return 0;
    const GAP = 8;
    const WINNER_V_PAD = 14; // pt-1.5 (6px) + pb-2 (8px)
    const NONWINNER_V_PAD = 16; // py-2 top + bottom (8px each)
    const hasW = winners.length > 0;
    const hasNW = nonWinners.length > 0;
    const rowsNW = hasNW ? Math.ceil(nonWinners.length / gridCols) : 0;
    let cardH;
    if (hasW && hasNW) {
      cardH = (cardsAreaH - WINNER_V_PAD - NONWINNER_V_PAD - (rowsNW - 1) * GAP) / (1 + rowsNW);
    } else if (hasW) {
      cardH = cardsAreaH - WINNER_V_PAD;
    } else if (hasNW) {
      cardH = (cardsAreaH - NONWINNER_V_PAD - (rowsNW - 1) * GAP) / rowsNW;
    } else {
      return 0;
    }
    return Math.max(160, cardH * (16 / 9));
  }, [showStats, cardsAreaH, winners.length, nonWinners.length, gridCols]);

  const gridMaxWidth = useMemo(() => {
    if (!unifiedCardW || nonWinners.length === 0) return undefined;
    return gridCols * unifiedCardW + (gridCols - 1) * 8;
  }, [unifiedCardW, nonWinners.length, gridCols]);

  async function handleNextRound() {
    setStarting(true);
    try {
      const nextChooserIndex = round.roundNumber % game.playerIds.length;
      const nextChooserId = game.playerIds[nextChooserIndex];
      await startRound(game.id, { chooserId: nextChooserId, currentRoundCount: game.roundCount });
    } catch (err) {
      logError(err);
      setStarting(false);
    }
  }

  async function handleKick(uid) {
    setKicking(true);
    try {
      await webrtc?.kickPlayer?.(uid);
    } catch (err) {
      logError(err);
    } finally {
      setKicking(false);
      setKickConfirmUid(null);
    }
  }

  async function handleEndGame() {
    setEnding(true);
    try {
      await endGame(game.id);
    } catch (err) {
      logError(err);
      setEnding(false);
    }
  }

  return (
    <div className={showStats
      ? "flex-1 min-h-0 overflow-y-auto"
      : "flex-1 min-h-0 flex flex-col overflow-hidden"
    }>

      {/* ── Cards area (winner + non-winner): flex-1 so controls stay at bottom ── */}
      <div
        ref={!showStats ? cardsAreaRef : undefined}
        className={!showStats ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : ''}
      >

      {/* ── Round winner(s) ─────────────────────────────────────────────── */}
      {winners.length > 0 && (
        <div className={`px-4 pt-1.5 pb-2${!showStats ? ' shrink-0' : ''}`}>
          <div className={`mx-auto ${regularPlayers.length > 2 ? 'w-[80%] sm:w-full sm:max-w-2xl' : 'w-full max-w-2xl'}`}>
            <div className={`flex gap-4 justify-center${unifiedCardW === 0 && winners.length === 1 ? ' max-w-xs mx-auto' : ''}`}>
              {winners.map((uid) => {
                const { avgErr, actualScore } = calcPlayerStats(uid, scores);
                return (
                  <div
                    key={uid}
                    className={unifiedCardW > 0 ? '' : 'flex-1 max-w-xs'}
                    style={unifiedCardW > 0 ? { width: unifiedCardW } : undefined}
                  >
                    <div className="relative">
                      <div className="absolute top-0 inset-x-0 z-10 flex justify-center pt-2 pointer-events-none">
                        <span className="bg-black/70 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide shadow">
                          {winners.length > 1 ? t('roundResults.roundWinners') : t('roundResults.roundWinner')}! <span className="normal-case">{t('roundResults.avgError')}: <span className="text-green-400">{avgErr}</span></span>
                        </span>
                      </div>
                      {uid === CHATTERS_ID ? (
                        <div className="aspect-video t-bg-input rounded-xl flex items-center justify-center">
                          <span className="text-2xl" aria-hidden="true">💬</span>
                        </div>
                      ) : hasWebRTC ? (
                        <PlayerVideoCard
                          uid={uid}
                          name={game.playerNames?.[uid] || uid}
                          avatarEmoji={playerAvatars?.[uid]}
                          isMe={uid === user?.uid}
                          isViewerChatter={isChatter}
                          isChattersSlot={false}
                          highlight={true}
                          {...webrtcCardProps(uid)}
                        />
                      ) : (
                        <div className="aspect-video t-bg-input rounded-xl flex items-center justify-center gap-2">
                          <span className="text-2xl select-none" aria-hidden="true">
                            {playerAvatars?.[uid] || '🎵'}
                          </span>
                          <span className="text-sm font-semibold t-text">{game.playerNames?.[uid]}</span>
                        </div>
                      )}
                      <div className="absolute bottom-0 inset-x-0 z-10 flex justify-center pb-2 pointer-events-none">
                        <span className="bg-black/70 text-white text-xs px-3 py-1 rounded-xl text-center shadow flex flex-col items-center gap-0.5">
                          <span className="font-semibold">{t('roundResults.actualRating')}: {actualScore}/10</span>
                          {winnerText && <span>{winnerText}</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Non-winner player grid ─────────────────────────────────────── */}
      {nonWinners.length > 0 && (
        <div
          className={showStats
            ? "px-4 py-2 max-w-2xl mx-auto w-full"
            : `${unifiedCardW > 0 ? 'shrink-0' : 'flex-1 min-h-0'} px-4 py-2 overflow-hidden`
          }
        >
          <div
            className="grid gap-2 w-full mx-auto"
            style={{
              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
              maxWidth: (!showStats && gridMaxWidth) ? `${gridMaxWidth}px` : undefined,
            }}
          >
            {nonWinners.map((uid) => {
              const { avgErr, actualScore } = calcPlayerStats(uid, scores);
              return (
                <div key={uid} className="relative">
                  {hasWebRTC ? (
                    <PlayerVideoCard
                      uid={uid}
                      name={game.playerNames?.[uid] || uid}
                      avatarEmoji={playerAvatars?.[uid]}
                      isMe={uid === user?.uid}
                      isViewerChatter={isChatter}
                      isChattersSlot={false}
                      {...webrtcCardProps(uid)}
                    />
                  ) : (
                    <div className="aspect-video t-bg-input rounded-xl flex items-center justify-center gap-2">
                      <span className="text-2xl select-none" aria-hidden="true">
                        {playerAvatars?.[uid] || '🎵'}
                      </span>
                      <span className="text-sm font-semibold t-text">{game.playerNames?.[uid]}</span>
                    </div>
                  )}
                  <div className="absolute top-0 inset-x-0 z-10 flex justify-center pt-2 pointer-events-none">
                    <span className="bg-black/70 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                      <span>{ordinal(nonWinnerPositions[uid])}</span>
                      <span className="normal-case font-normal"> · {t('roundResults.avgError')}: <span className="text-red-400">{avgErr}</span></span>
                    </span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 z-10 flex justify-center pb-2 pointer-events-none">
                    <span className="bg-black/70 text-white text-xs font-semibold px-3 py-1 rounded-full text-center shadow">
                      {t('roundResults.actualRating')}: {actualScore}/10
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      </div>{/* end cards area */}

      {/* ── Host controls ──────────────────────────────────────────────── */}
      {isHost && (
        <div className={`px-4 pt-2 pb-3${!showStats ? ' shrink-0' : ''}`}>
          <div className="max-w-lg mx-auto w-full space-y-3">
            <div className="flex gap-3">
              <button
                onClick={handleNextRound}
                disabled={starting || endGameConfirm}
                className="flex-1 t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
              >
                {starting ? t('roundResults.starting') : t('roundResults.nextRound')}
              </button>
              {!endGameConfirm ? (
                <button
                  onClick={() => setEndGameConfirm(true)}
                  disabled={starting}
                  className="flex-1 t-bg-input hover:opacity-80 disabled:opacity-50 t-text font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
                >
                  {t('roundResults.endGame')}
                </button>
              ) : (
                <div className="flex-1 t-bg-surface t-border border rounded-lg px-4 py-3 space-y-2">
                  <p className="text-sm t-text-error font-medium">{t('roundResults.endGameConfirm')}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleEndGame}
                      disabled={ending}
                      className="text-sm t-btn-error disabled:opacity-50 font-semibold px-3 py-1.5 rounded transition-colors t-focus-ring"
                    >
                      {ending ? t('roundResults.ending') : t('roundResults.yesEndGame')}
                    </button>
                    <button
                      onClick={() => setEndGameConfirm(false)}
                      disabled={ending}
                      className="text-sm t-text-disabled hover:t-text-muted transition-colors t-focus-ring rounded px-2 py-1.5"
                    >
                      {t('roundResults.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Round stats toggle ─────────────────────────────────────────── */}
      <div className={`px-4 pb-3${!showStats ? ' shrink-0' : ''}`}>
        <div className="max-w-lg mx-auto w-full">
          <button
            onClick={toggleStats}
            aria-expanded={showStats}
            className="w-full text-sm t-text-muted hover:t-text flex items-center justify-center gap-1.5 py-1.5 transition-colors t-focus-ring rounded"
          >
            <span>{t('roundResults.roundStats')}</span>
            <span aria-hidden="true">{showStats ? '▲' : '▾'}</span>
          </button>
        </div>
      </div>

      {/* ── Round stats table ──────────────────────────────────────────── */}
      {showStats && (
        <div className="px-4 pt-0 pb-4">
          <div className="t-border border rounded-2xl overflow-hidden max-w-2xl mx-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('roundResults.statsCaption')}</caption>
                <thead className="t-bg-surface">
                  <tr className="t-border border-b">
                    <th scope="col" className="px-3 py-2.5 text-xs font-medium t-text-disabled uppercase tracking-wider text-left whitespace-nowrap">
                      {t('roundResults.player')}
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-xs font-medium t-text-disabled uppercase tracking-wider text-center whitespace-nowrap">
                      {t('roundResults.actualRating')}
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-xs font-medium t-text-disabled uppercase tracking-wider text-left whitespace-nowrap">
                      {t('roundResults.colPredictions')}
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-xs font-medium t-text-disabled uppercase tracking-wider text-center whitespace-nowrap">
                      {t('roundResults.avgError')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((uid) => {
                    const { avgErr, actualScore } = calcPlayerStats(uid, scores);
                    const isWinner = winners.includes(uid);
                    const myPredictions = scores[uid]?.predictions || {};
                    const predItems = game.playerIds
                      .filter(id => id !== uid && myPredictions[id] != null)
                      .map(id => `${game.playerNames?.[id] ?? id}: ${myPredictions[id]}`);
                    return (
                      <tr key={uid} className={`t-border border-b last:border-0 ${isWinner ? 't-bg-accent-muted' : ''}`}>
                        <td className="px-3 py-2.5 t-text font-medium whitespace-nowrap">
                          {isWinner && <span aria-hidden="true">🏆 </span>}
                          {uid === CHATTERS_ID && <span aria-hidden="true">💬 </span>}
                          {game.playerNames?.[uid] ?? uid}
                        </td>
                        <td className="px-3 py-2.5 text-center font-bold t-text">
                          {actualScore}
                        </td>
                        <td className="px-3 py-2.5 t-text-secondary text-xs">
                          {predItems.length > 0 ? predItems.join(' · ') : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-center font-semibold t-text">
                          {avgErr}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {kickConfirmUid && (
        <KickDialog
          name={game.playerNames?.[kickConfirmUid] || kickConfirmUid}
          onConfirm={() => handleKick(kickConfirmUid)}
          onCancel={() => setKickConfirmUid(null)}
          kicking={kicking}
        />
      )}
    </div>
  );
}
